// src/components/Layout.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useMessages } from '../context/MessagesContext';
import EmailVerificationBanner from './EmailVerificationBanner';
import BetaBanner from './BetaBanner';
import WhatsNewModal from './WhatsNewModal';
import {
  AppBar,
  Box,
  Drawer,
  Toolbar,
  Typography,
  Button,
  Container,
  IconButton,
  Menu,
  MenuItem,
  Badge,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Message as MessageIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  HelpOutline as HelpIcon,
  School as TrainingIcon,
  Feedback as FeedbackIcon,
  Menu as MenuIcon,
  NewReleases as WhatsNewIcon,
} from '@mui/icons-material';
import Avatar from '@mui/material/Avatar';

const API_ORIGIN = (process.env.REACT_APP_API_URL || 'http://localhost:8080').replace(/\/$/, '');

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return parts[0][0].toUpperCase();
};

const Layout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { stats, loadStats } = useMessages();
  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [version, setVersion] = useState('');

  // Load stats on mount
  useEffect(() => {
    if (user) {
      loadStats();
    }
  }, [user, loadStats]);

  // Fetch API version on mount
  useEffect(() => {
    fetch(`${API_ORIGIN}/api/version`)
      .then(res => res.json())
      .then(data => setVersion(data.version))
      .catch(() => setVersion(''));
  }, []);

  const unreadCount = stats?.unread_count || 0;

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleClose();
    await logout();
    navigate('/login');
  };

  const handleSettings = () => {
    handleClose();
    navigate('/settings');
  };

  // Check if user is a caregiver (not self-signup)
  // Only caregivers need the People menu
  const isCaregiver = ['owner', 'caregiver', 'self'].includes(user?.accountType);

  // Build nav items dynamically
  const navItems = [
    { label: 'Dashboard', path: '/dashboard', icon: <DashboardIcon /> },
    // Only show People for caregiver accounts
    ...(isCaregiver ? [{ label: 'People', path: '/people', icon: <PeopleIcon /> }] : []),
    { label: 'Messages', path: '/messages', icon: <MessageIcon /> },
    { label: 'Training', path: '/training', icon: <TrainingIcon /> },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar position="static">
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          <Typography
            variant="h6"
            component="div"
            onClick={() => navigate('/dashboard')}
            sx={{ flexGrow: 0, mr: 4, cursor: 'pointer', userSelect: 'none' }}
          >
            Second Clear
          </Typography>

          <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' }, gap: 2 }}>
            {navItems.map((item) => (
              <Button
                key={item.path || item.label}
                color="inherit"
                startIcon={
                  item.path === '/messages' && unreadCount > 0 ? (
                    <Badge badgeContent={unreadCount} color="error">
                      {item.icon}
                    </Badge>
                  ) : (
                    item.icon
                  )
                }
                onClick={() => item.external ? window.open(item.external, '_blank') : navigate(item.path)}
                sx={{
                  borderBottom: item.path && location.pathname === item.path ? '2px solid white' : 'none',
                }}
              >
                {item.label}
              </Button>
            ))}
          </Box>

          <Box sx={{ flexGrow: { xs: 1, md: 0 } }} />

          <Box>
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenu}
              color="inherit"
              sx={{ p: 0.5 }}
            >
              <Avatar
                sx={{
                  width: 34,
                  height: 34,
                  bgcolor: 'white',
                  color: 'primary.main',
                  fontSize: 14,
                  fontWeight: 700,
                }}
              >
                {getInitials(user?.name)}
              </Avatar>
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={handleClose}
            >
              <MenuItem disabled>
                <Typography variant="body2" color="text.secondary">
                  {user?.name || user?.email}
                </Typography>
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleSettings}>
                <ListItemIcon>
                  <SettingsIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Settings</ListItemText>
              </MenuItem>
              <MenuItem onClick={() => { handleClose(); navigate('/help'); }}>
                <ListItemIcon>
                  <HelpIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Help Center</ListItemText>
              </MenuItem>
              <MenuItem onClick={() => { handleClose(); navigate('/feedback'); }}>
                <ListItemIcon>
                  <FeedbackIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Feedback</ListItemText>
              </MenuItem>
              <MenuItem onClick={() => { handleClose(); navigate('/whats-new'); }}>
                <ListItemIcon>
                  <WhatsNewIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>What's New</ListItemText>
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Logout</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 240 },
        }}
      >
        <Toolbar>
          <Typography variant="h6" fontWeight={600} onClick={() => { navigate('/dashboard'); setMobileOpen(false); }} sx={{ cursor: 'pointer' }}>
            Second Clear
          </Typography>
        </Toolbar>
        <Divider />
        <List>
          {navItems.map((item) => (
            <ListItem key={item.path || item.label} disablePadding>
              <ListItemButton
                selected={item.path && location.pathname === item.path}
                onClick={() => {
                  if (item.external) {
                    window.open(item.external, '_blank');
                  } else {
                    navigate(item.path);
                  }
                  setMobileOpen(false);
                }}
              >
                <ListItemIcon>
                  {item.path === '/messages' && unreadCount > 0 ? (
                    <Badge badgeContent={unreadCount} color="error">
                      {item.icon}
                    </Badge>
                  ) : (
                    item.icon
                  )}
                </ListItemIcon>
                <ListItemText primary={item.label} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Drawer>

      <EmailVerificationBanner />
      <BetaBanner />
      <WhatsNewModal />

      <Container maxWidth="xl" sx={{ mt: { xs: 2, sm: 4 }, mb: 4, flexGrow: 1, overflowX: 'hidden' }}>
        {children}
      </Container>

      <Box
        component="footer"
        sx={{
          py: 3,
          px: 2,
          mt: 'auto',
          backgroundColor: (theme) =>
            theme.palette.mode === 'light'
              ? theme.palette.grey[200]
              : theme.palette.grey[800],
        }}
      >
        <Container maxWidth="sm">
          <Typography variant="body2" color="text.secondary" align="center">
            © 2026 Second Clear - Fraud Prevention Dashboard
            {version && (
              <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.disabled' }}>
                v{version}
              </Typography>
            )}
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default Layout;
